import { useEffect, useState, useCallback } from "react";
import { GridRowData } from "@mui/x-data-grid";

import LineChart from "../../components/lineChart";
import Table from "../../components/table";
import PeriodTab from "../../components/periodTab";
import DateTimeRangePicker from "../../components/dateTimeRangePicker";

import { sensorApi, devicesApi } from "../../api";
import { SensorReadings } from "../../api/interfaces";
import { Period } from "../../api/types/sensor";
import { DeviceRangeReadingsGranularity } from "../../api/types/devices";

import classes from "./index.module.scss";

type Props = {
  pageTitle: string;
  sensorType: "pm25" | "temp" | "humidity" | "co2";
  device_id?: string;
  showDatePicker?: boolean;
};

const dataTableColumns = [
  { field: "uid", hide: true },
  { field: "period", headerName: "Period", type: "date", width: 250 },
  { field: "reading", headerName: "Reading", type: "number", width: 200 },
  {
    field: "sensor",
    headerName: "Sensor",
    type: "string",
    sortable: false,
    flex: 1,
  },
];

const Data = ({ pageTitle, sensorType, device_id, showDatePicker }: Props) => {
  const [tableData, setTableData] = useState<GridRowData[]>([]);
  const [data, setData] = useState<SensorReadings[]>([]);
  const [period, setPeriod] = useState<Period>("day");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<string>();
  const [toDate, setToDate] = useState<string>();
  const [rangeGranularity, setRangeGranularity] =
    useState<DeviceRangeReadingsGranularity>();

  const formatTableData = useCallback((items: SensorReadings[]) => {
    return items.map((el, i) => {
      return {
        uid: i,
        sensor: el.sensor_name,
        reading: el.reading,
        period:
          (showDatePicker && rangeGranularity !== "1d") || period === "day"
            ? new Date(el.period).toLocaleString()
            : new Date(el.period).toDateString(),
      };
    });
  }, [period]);

  useEffect(() => {
    const getSensorReadings = async () => {
      setIsLoading(true);

      if (device_id) {
        // Individual unit data page
        await sensorApi
          .getUnitReadings({ device_id, period, sensorType })
          .then((data) => {
            if (data.status < 400) {
              const { readings } = data.params;
              setData(readings);
              setTableData(formatTableData(readings));
            }

            setIsLoading(false);
          });
      } else {
        // all units average data page
        await sensorApi
          .getAllUnitsAvgReadings({ period, sensorType })
          .then((data) => {
            if (data.status < 400) {
              const { readings } = data.params;
              setData(readings);
              setTableData(formatTableData(readings));
            }

            setIsLoading(false);
          });
      }
    };

    getSensorReadings();
  }, [period, sensorType, device_id]);

  useEffect(() => {
    const getReadingsInRange = async () => {
      setIsLoading(true);

      if (device_id && fromDate && toDate && rangeGranularity)
        await devicesApi
          .getFromToDeviceReadings({
            from: fromDate,
            to: toDate,
            granularity: rangeGranularity,
            device_id,
            sensorType,
          })
          .then((data) => {
            if (data.status < 400) {
              const readings = data.params;
              setData(readings);
              setTableData(formatTableData(readings));
            }

            setIsLoading(false);
          });
    };

    getReadingsInRange();
  }, [fromDate, toDate, rangeGranularity, device_id, sensorType]);

  const handleRangeUpdate = (
    from: string,
    to: string,
    granularity: DeviceRangeReadingsGranularity
  ) => {
    setFromDate(from);
    setToDate(to);
    setRangeGranularity(granularity);
  };

  return (
    <div
      style={{ ...(device_id && { paddingLeft: 0 }) }}
      className={classes.root}
    >
      <div style={{ display: "flex" }}>
        <div className={classes.chartSection}>
          {showDatePicker ? (
            <DateTimeRangePicker onChange={handleRangeUpdate} />
          ) : (
            <PeriodTab
              sensor={pageTitle}
              handleSelect={setPeriod}
              selected={period}
            />
          )}
          <div className={classes.chartContainer}>
            <LineChart
              {...{ sensorType, data, period, isLoading }}
              containerHeight="40vh"
            />
          </div>
        </div>
        <Table
          {...{ isLoading }}
          columns={dataTableColumns}
          dataSet={tableData}
          showSearch={false}
        />
      </div>
    </div>
  );
};

export default Data;
